import type { GlobalPermission } from "@superagents/types/api";
import { RoutePaths } from "@/core/constants/RoutePaths";
import type { PlanFeatureKey } from "@/core/hooks/planFeature";
import {
  LOCKABLE_PAGE_COPY,
  pagePlanLocksPath,
  type LockablePagePermission,
  type PagePlanLock,
} from "./FeatureGate/pageCopy";

/** Para onde o bloqueio de recurso devolve a pessoa, e com que nome. */
export type GateReturn = {
  /** Nome da página no menu: o botão diz "Voltar para {label}". */
  label: string;
  /** Pathname completo da origem (sub-rota e conversa aberta inclusas). */
  to: string;
};

/** `planFeature`/`planScope`/`planActionRoutes`: a trava de plano da página. */
export type PageSection = PagePlanLock & {
  route: string;
  label: string;
  /** Chave que o PermissionGate da rota exige. Sem ela: nunca bloqueada. */
  permission?: LockablePagePermission;
};

/* Seções do MainLayout, espelhando os PermissionGate do AppRoutes. Conversas
   nunca é bloqueada (D9). Uma página nova com gate entra em
   LOCKABLE_PAGE_COPY (o tipo obriga a rota) e vem para cá sozinha. Os nomes são os do menu: o teste
   menuLabelParity falha se um deles divergir da Navbar. Exceção: Áreas é aba
   de Equipe (`/team-management/areas`), com o nome da aba. */
export const PAGE_SECTIONS: readonly PageSection[] = [
  { route: RoutePaths.CHATS, label: "Conversas" },
  ...(
    Object.entries(LOCKABLE_PAGE_COPY) as [
      LockablePagePermission,
      (typeof LOCKABLE_PAGE_COPY)[LockablePagePermission],
    ][]
  ).map(
    ([
      permission,
      { route, label, planFeature, planScope, planActionRoutes },
    ]) => ({
      route,
      label,
      permission,
      planFeature,
      planScope,
      planActionRoutes,
    })
  ),
];

/** Sem origem conhecida, o bloqueio devolve para Conversas. */
export const DEFAULT_GATE_RETURN: GateReturn = {
  label: "Conversas",
  to: RoutePaths.CHATS,
};

/** Seções que contêm o pathname: a própria rota ou uma sub-rota dela. */
const sectionsOf = (pathname: string) =>
  PAGE_SECTIONS.filter(
    ({ route }) => pathname === route || pathname.startsWith(`${route}/`)
  );

/**
 * Seção de um pathname: a própria rota do menu ou uma sub-rota dela. Com duas
 * (Áreas dentro de Equipe), vale a mais específica.
 */
export function findPageSection(pathname: string): PageSection | undefined {
  return sectionsOf(pathname).reduce<PageSection | undefined>(
    (best, section) =>
      !best || section.route.length > best.route.length ? section : best,
    undefined
  );
}

/**
 * Recurso de plano que decide se este pathname abre: `null` quando a trava de
 * plano da seção não vale nele (sem recurso, ou página que só trava a ação).
 * Quem chama pede só este ao plano, e nada espera o plano à toa.
 */
export function planFeatureForPath(pathname: string): PlanFeatureKey | null {
  const section = findPageSection(pathname);
  if (!section?.planFeature || !pagePlanLocksPath(section, pathname)) {
    return null;
  }
  return section.planFeature;
}

const canOpen = (
  section: PageSection,
  pathname: string,
  permissions: readonly GlobalPermission[],
  isPlanLocked: boolean
) =>
  // Integrações (`planScope: "actions"`) só trava a rota de instalar.
  !(isPlanLocked && pagePlanLocksPath(section, pathname)) &&
  // Rota aninhada passa por todos os gates do caminho: Áreas exige também a
  // chave de Equipe (ruling R11).
  sectionsOf(pathname).every(
    ({ permission }) => !permission || permissions.includes(permission)
  );

/**
 * A página abre com estas permissões (o gate dela está liberado)?
 * `isPlanLocked`: o recurso de plano da página (`planFeature`) está travado,
 * ou ainda não se sabe (quem chama passa `true` enquanto o plano carrega).
 */
export function isPageAccessible(
  pathname: string,
  permissions: readonly GlobalPermission[],
  isPlanLocked = false
): boolean {
  const section = findPageSection(pathname);
  return !!section && canOpen(section, pathname, permissions, isPlanLocked);
}

/**
 * Destino do "Voltar para ..." do bloqueio: a última página liberada em que a
 * pessoa estava (`originPath`), conferida de novo com as permissões e o plano
 * de agora (`isPlanLocked`, do recurso de plano da origem). Sem origem, origem
 * fora das seções do app ou origem que ficou bloqueada: Conversas. Nunca
 * `navigate(-1)`, que pode sair do app ou cair em outro bloqueio.
 */
export function resolveGateReturn(
  originPath: string | null,
  permissions: readonly GlobalPermission[],
  isPlanLocked = false
): GateReturn {
  const section = originPath ? findPageSection(originPath) : undefined;
  if (
    !originPath ||
    !section ||
    !canOpen(section, originPath, permissions, isPlanLocked)
  ) {
    return DEFAULT_GATE_RETURN;
  }
  return { label: section.label, to: originPath };
}
